import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { can, useOrg } from "../org";
import { DatePick, Field } from "../people/bits";
import { ContactActions } from "./LogCare";
import { PersonAssignments } from "./Assignments";
import { dayLabel, fmtInChurch, methodLabel } from "./text";
import { VoicePlayback } from "./Voice";

const NeedDialog = ({ person, onClose, onSaved }) => {
  const { api } = useOrg();
  const [f, setF] = useState({ title: "", context: "", next_step: "", next_step_on: "" });
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = () => api(`/people/${person.id}/needs`, { method: "POST", body: { ...f, next_step_on: f.next_step_on || null } })
    .then(() => { toast.success("Care need opened"); onSaved(); }).catch((e) => toast.error(e.message));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-paper max-w-md" data-testid="need-dialog">
        <DialogTitle className="t-name-lg">Open a care need for {person.first_name}</DialogTitle>
        <DialogDescription className="t-meta">Keep it short. {person.caregiver ? `${person.caregiver.name} will own it.` : "You'll own it until a caregiver is assigned."}</DialogDescription>
        <Field label="Reason for care"><input value={f.title} onChange={set("title")} placeholder="e.g. Home from the hospital" className="field h-12 px-3" data-testid="need-title" /></Field>
        <Field label="Brief context (optional)"><textarea value={f.context} onChange={set("context")} rows={2} className="field p-3" data-testid="need-context" /></Field>
        <div className="grid grid-cols-[1fr_auto] gap-2">
          <Field label="Next step (optional)"><input value={f.next_step} onChange={set("next_step")} placeholder="e.g. Call after surgery" className="field h-12 px-3" data-testid="need-next-step" /></Field>
          <Field label="By"><DatePick value={f.next_step_on} onPick={(v) => setF((x) => ({ ...x, next_step_on: v }))} placeholder="Date" testid="need-next-step-on" className="!h-12" /></Field>
        </div>
        <button disabled={!f.title.trim()} onClick={save} className="btn btn-primary w-full" data-testid="need-save">Open care need</button>
      </DialogContent>
    </Dialog>
  );
};

const Needs = ({ care, c, reload, onOpen }) => {
  const { api } = useOrg();
  const open = care.needs.filter((n) => n.status === "open");
  const resolve = (n) => api(`/needs/${n.id}`, { method: "PATCH", body: { status: "resolved" } }).then(() => { toast.success("Care need resolved"); reload(); }).catch((e) => toast.error(e.message));
  return (
    <section className="py-7 border-t border-line" data-testid="person-needs">
      <div className="flex items-center justify-between gap-3 mb-3"><h2 className="t-label">Care needs</h2><button onClick={onOpen} className="btn btn-quiet btn-sm" data-testid="person-open-need-btn">Open care need</button></div>
      {open.map((n) => {
        const fu = care.follow_ups.find((f) => f.care_need_id === n.id);
        return (
        <div key={n.id} className="thread thread-active py-2 mb-3" data-testid={`need-${n.id}`}>
          <p className="t-name-sm">{n.title}</p>
          {n.context && <p className="t-body text-ink-soft mt-1">{n.context}</p>}
          <p className="t-meta mt-1">Opened by {n.opened_by_name} · {fmtInChurch(n.opened_at, { month: "short", day: "numeric" })} · Owned by {n.owner_name}</p>
          {fu && <p className="status status-attention mt-2" data-testid={`need-next-${n.id}`}>{fu.reason} · {dayLabel(fu.due_on)}</p>}
          {c.oversee && <button onClick={() => resolve(n)} className="btn btn-outline btn-sm mt-3" data-testid={`need-resolve-${n.id}`}>Mark resolved</button>}
        </div>
        );
      })}
      {!open.length && <p className="t-meta">No open care needs.</p>}
    </section>
  );
};

const FollowUps = ({ care, reload }) => {
  const { api } = useOrg();
  const move = (f, due_on) => api(`/follow-ups/${f.id}`, { method: "PATCH", body: due_on ? { due_on } : { status: "cancelled" } }).then(() => { toast.success(due_on ? "Follow-up rescheduled" : "Follow-up removed"); reload(); }).catch((e) => toast.error(e.message));
  if (!care.follow_ups.length) return null;
  return (
    <section className="py-7 border-t border-line" data-testid="person-follow-ups">
      <h2 className="t-label mb-3">Follow-ups</h2>
      {care.follow_ups.map((f) => (
        <div key={f.id} className="flex flex-wrap items-center justify-between gap-3 py-2" data-testid={`follow-up-${f.id}`}>
          <div><p className="t-body">{f.reason}</p><p className="t-meta">{dayLabel(f.due_on)} · {f.assignee_name}</p></div>
          <div className="flex gap-2">
            <DatePick value={null} onPick={(v) => move(f, v)} placeholder="Reschedule" testid={`follow-up-reschedule-${f.id}`} />
            <button onClick={() => move(f, null)} className="btn btn-quiet btn-sm" data-testid={`follow-up-cancel-${f.id}`}>Remove</button>
          </div>
        </div>
      ))}
    </section>
  );
};

const Story = ({ p, care }) => (
  <section className="py-7 border-t border-line" data-testid="person-story">
    <h2 className="t-heading">{p.preferred_name || p.first_name}'s Story</h2>
    <ol className="mt-5 relative border-l border-line ml-2">
      {care.story.map((i) => (
        <li key={i.id} className="pl-6 pb-6 relative" data-testid={`story-${i.id}`}>
          <span className="absolute -left-[5px] top-2 h-2.5 w-2.5 rounded-full bg-brand" />
          <p className="t-body"><span className="font-semibold">{methodLabel(i.method)}</span> with {i.by_name}</p>
          <p className="t-meta">{fmtInChurch(i.at, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</p>
          {i.note && <p className="t-body text-ink-soft mt-2 max-w-prose">{i.note}</p>}
          {i.voice && <VoicePlayback voice={i.voice} />}
        </li>
      ))}
      {!care.story.length && <li className="pl-6 t-meta">No care connection recorded yet.</li>}
    </ol>
  </section>
);

export const PersonCare = ({ p }) => {
  const { api, membership } = useOrg();
  const c = can(membership);
  const [care, setCare] = useState(null);
  const [needOpen, setNeedOpen] = useState(false);
  const load = useCallback(() => api(`/people/${p.id}/care`).then(setCare).catch(() => {}), [api, p.id]);
  useEffect(() => { load(); const on = () => load(); window.addEventListener("care:logged", on); return () => window.removeEventListener("care:logged", on); }, [load]);
  if (!care) return null;
  const needId = care.needs.find((n) => n.status === "open")?.id;
  return (
    <div data-testid="person-care">
      <ContactActions p={p} careNeedId={needId} className="mt-6 max-w-md" />
      <div className="mt-8">
        <Needs care={care} c={c} reload={load} onOpen={() => setNeedOpen(true)} />
        <PersonAssignments p={p} c={c} needId={needId} />
        <FollowUps care={care} reload={load} />
        <Story p={p} care={care} />
      </div>
      {needOpen && <NeedDialog person={p} onClose={() => setNeedOpen(false)} onSaved={() => { setNeedOpen(false); load(); }} />}
    </div>
  );
};
